import React from 'react';
import * as yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Formik, FormikHelpers, FormikValues } from 'formik';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  styled,
  Typography,
} from '@mui/material';
// Components
import { FormikCheckBox, FormikCountry, FormikField } from '../formik';
import { Message } from '@components';
// Utils
import { Api, setToken } from '@utils';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuthContext } from './AuthContext';
import { useQuery } from '@/hooks';

// ----------------------------------------------------------------------
type Props = {
  privacy: string;
};

const validationSchema = yup.object({
  email: yup
    .string()
    .email('Invalid email address')
    .matches(
      /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/,
      'Invalid email address'
    )
    .required('Email is required')
    .trim(),
  name: yup.string().required('Full name is required').min(2).max(20).trim(),
  country: yup.string().required('Country is required'),
  password: yup
    .string()
    .required('Password is required')
    .min(8)
    .max(20)
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,20}$/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number and one special character'
    )
    .trim(),
  referral: yup.string().trim(),
});

const SignIn = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  textAlign: 'center',
  marginTop: theme.spacing(3),

  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    opacity: 0.8,
    transition: 'all 0.3s ease-in-out',

    '&:hover': {
      opacity: 1,
      color: theme.palette.text.primary,
    },
  },
}));

const RegForm: React.FC<Props> = ({ privacy }) => {
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);

  const query = useQuery();
  const referral = query.get('referral');

  const initialValues = {
    email: '',
    name: '',
    country: 'CA',
    password: '',
    checkbox: false,
    referral: referral || '',
  };

  const navigate = useNavigate();
  const { setIsAuthenticated } = useAuthContext();

  React.useEffect(() => {
    if (success) {
      setTimeout(() => {
        navigate(`/verify?email=${email}`, { replace: true });
      }, 1000);
    }
  }, [success]);

  // Handle form submit
  const handleSubmitForm = async (
    values: FormikValues,
    helpers: FormikHelpers<FormikValues> | any
  ) => {
    setEmail(values.email);
    values.redirect = localStorage.getItem('redirect') || '';
    // Reset cache
    localStorage.removeItem('steps');
    localStorage.removeItem('token');
    localStorage.removeItem('sidToken');

    try {
      const { data } = await Api.post('/register', values);
      if (data.success) {
        // Save token to local storage
        setIsAuthenticated(true);
        setToken(data.data.token);

        // Save the redirect
        // await saveRedirect(data.data.token);

        setSuccess(true);
        setError('');
        helpers.resetForm();
      }
    } catch (error: any) {
      setError(error);
      setToken('');
      setSuccess(false);
    }

    helpers.setTouched({});
  };

  const saveRedirect = async (token: string) => {
    // get query string from url
    const queryString = window.location.search;

    // Check if query string contains hmac, code, host, shop
    if (
      queryString.includes('hmac') &&
      queryString.includes('code') &&
      queryString.includes('host') &&
      queryString.includes('shop')
    ) {
      // save to db
      const { data } = await Api.put(
        '/profile',
        { redirect: queryString },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        // Save to localStorage
        localStorage.setItem('redirect', queryString);
      }
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmitForm}
    >
      {({ isSubmitting }) => (
        <Form autoComplete="off">
          <FormikField
            name="email"
            label="Email"
            type="email"
            placeholder="Enter your email"
          />

          <FormikField
            name="name"
            label="Full name"
            type="text"
            placeholder="Enter your full name"
          />

          <FormikCountry name="country" label="Country" maxHeight={224} />

          <FormikField
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your password"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {(!referral || referral === '' || referral === null) && (
            <FormikField
              name="referral"
              label="Referral Code (Optional)"
              type="text"
              placeholder="Enter your referral code"
            />
          )}

          <Box sx={{ my: 5 }}>
            <FormikCheckBox
              name="checkbox"
              label={
                <Typography variant="body2">
                  Get emails from {import.meta.env.VITE_SITE} about product
                  updates, industry news, and events. If you change your mind,
                  you can unsubscribe at any time.{' '}
                  <a href={privacy}>Privacy Policy</a>
                </Typography>
              }
            />
          </Box>

          <Button
            type="submit"
            disabled={isSubmitting}
            variant="contained"
            color="primary"
            fullWidth
          >
            Create account
          </Button>

          {error && <Message sx={{ mt: 3 }} type="error" title={error} />}
          {!error && success && (
            <Message
              sx={{ mt: 3 }}
              type="success"
              title="Register successfully! Redirecting..."
            />
          )}

          <SignIn variant="body2">
            Have an account? <Link to="/login">Sign in</Link>
          </SignIn>
        </Form>
      )}
    </Formik>
  );
};

export default RegForm;
